import { createInsertSchema } from "../../utils/sql";
import { z } from "zod/v4";
import { zTimezone } from "../../utils/form-schema";
import { withZeroTransaction } from "../../zero/utils";
import { zql, type Schema } from "../../zero/schema";
import { defineQuery } from "../../zero/transformer";
import { type Transaction, type ExpressionBuilder, type Row, type ZeRow } from "@rocicorp/zero";
import { foodLogEntry } from "./food-log.sql";
import { AccessGrant } from "@corpuscomplete/core/auth/grant";
import { Food } from "../food";
import type { UserActor } from "@corpuscomplete/core/actor";
import { accessDenied } from "@corpuscomplete/core/utils";
import { foodLogSourceTypes } from "./constants";
import { MealPlan } from "../meal-plan";
import { PlannedMeal } from "../planned-meal";
import { temporalInstantSchema } from "../../common/date-time/zod";

/**
 * Food-log namespace boundary for user intake history and suggestions.
 *
 * Invariants:
 * - Cross-user access is gated by user-scoped grants.
 * - Suggestion ranking uses bounded recency/frequency and time-of-day windows.
 *
 * @see `packages/core/docs/namespace.md`
 */
export namespace FoodLog {
  export type EntryRow = Row<ReturnType<typeof onDate.fn>>;
  export type Source = {
    label: string;
    items: readonly Food.Item[];
    status: "resolved" | "missing";
  };

  export type Entry = Omit<Food.AsItem<EntryRow>, "sourceGroup"> & { kind: "entry" };
  export type SourceGroup = ZeRow<typeof zql.foodLogSourceGroup> & {
    entries: readonly Entry[];
    source: Source;
    kind: "sourceGroup";
  };
  export type SourceGroupCandidate = {
    kind: "sourceGroupCandidate";
    id: string;
    timeAt: Temporal.Instant;
    label: string;
    items: readonly Food.Item[];
    sourceId: string;
    sourceType: SourceType;
  };
  export type SourceGroupCandidateGroup = {
    kind: "sourceGroupCandidateGroup";
    id: string;
    timeAt: Temporal.Instant;
    label: string;
    candidates: readonly SourceGroupCandidate[];
  };
  export type SourceGroupCandidateLike = SourceGroupCandidate | SourceGroupCandidateGroup;
  export type SourceGroupContribution = {
    candidates: readonly SourceGroupCandidateLike[];
    sources: ReadonlyMap<string, Source>;
    defaultLabel: string;
  };

  export type Item = Entry | SourceGroup | SourceGroupCandidate | SourceGroupCandidateGroup;

  export const sourceTypeSchema = z.enum(foodLogSourceTypes);
  export type SourceType = (typeof foodLogSourceTypes)[number];

  export function items(
    rows: readonly EntryRow[],
    sourceContribution: SourceGroupContribution,
  ): readonly Item[] {
    const results: Item[] = [];
    // Just so we can push to the entries array in the function
    type SourceGroupWithMutableEntries = ZeRow<typeof zql.foodLogSourceGroup> & {
      entries: Entry[];
    };
    const sourceGroups = new Map<string, SourceGroupWithMutableEntries>();

    for (const row of rows) {
      const item = Food.asItem(row);
      if (!item) {
        continue;
      }
      if (row.sourceGroupId && row.sourceGroup) {
        let sourceGroup = sourceGroups.get(row.sourceGroupId);
        if (!sourceGroup) {
          sourceGroup = {
            ...row.sourceGroup,
            entries: [],
          };
          sourceGroups.set(row.sourceGroupId, sourceGroup);
          results.push({
            ...sourceGroup,
            kind: "sourceGroup",
            source: sourceContribution.sources.get(sourceGroup.sourceId) ?? {
              label: sourceContribution.defaultLabel,
              items: [],
              status: "missing",
            },
          });
        }
        sourceGroup.entries.push({
          ...item,
          kind: "entry",
        });
      } else {
        results.push({
          ...item,
          kind: "entry",
        });
      }
    }

    results.push(...sourceContribution.candidates);

    return results;
  }

  export const onDate = defineQuery(
    z.object({
      date: z.iso.date(),
      timeZone: zTimezone(),
      userId: z.uuid(),
    }),
    ({ ctx, args: { date, timeZone, userId } }) => {
      const plainDate = Temporal.PlainDate.from(date);
      const start = plainDate.toZonedDateTime({
        timeZone,
      });
      const end = plainDate.add({ days: 1 }).toZonedDateTime({
        timeZone,
      });
      const q = zql.foodLogEntry
        .where("userId", "=", userId)
        .where("timeAt", ">=", start.toInstant())
        .where("timeAt", "<", end.toInstant())
        .related("serving", (q) => q.related("food", (q) => q.related("meta")))
        .related("sourceGroup")
        .orderBy("timeAt", "asc");
      if (userId === ctx.actor.userId) {
        return q;
      }
      return q.where((e) => viewable(e, ctx.actor));
    },
  );

  export const forSuggestions = defineQuery(
    z.object({
      userId: z.uuid(),
    }),
    ({ ctx, args: { userId } }) => {
      const now = Temporal.Now.instant();
      const historyStart = now
        .toZonedDateTimeISO("UTC")
        .subtract({ days: HISTORY_DAYS })
        .toInstant();

      const q = zql.foodLogEntry
        .where("userId", "=", userId)
        .where("timeAt", ">=", historyStart)
        .related("serving", (serving) => serving.related("food", (food) => food.related("meta")));
      if (userId === ctx.actor.userId) {
        return q;
      }
      return q.where((e) => viewable(e, ctx.actor));
    },
  );

  const TIME_WINDOW_HOURS = 2;
  const HISTORY_DAYS = 30;
  const SUGGESTIONS_LIMIT = 20;

  function isWithinTimeWindow(
    entryTimeAt: Temporal.Instant,
    entryTimeZone: string,
    targetTime: Temporal.PlainTime,
  ): boolean {
    const entryZdt = entryTimeAt.toZonedDateTimeISO(entryTimeZone);
    const entryMinutes = entryZdt.hour * 60 + entryZdt.minute;
    const targetMinutes = targetTime.hour * 60 + targetTime.minute;

    let diff = Math.abs(entryMinutes - targetMinutes);
    if (diff > 12 * 60) {
      diff = 24 * 60 - diff;
    }
    return diff <= TIME_WINDOW_HOURS * 60;
  }

  export function suggestions(
    entries: readonly Omit<Entry, "kind">[],
    time: Temporal.PlainTime,
  ): Food.Item[] {
    const byFoodId = new Map<
      string,
      { entry: Omit<Entry, "kind">; frequency: number; mostRecentTimeAt: Temporal.Instant }
    >();

    for (const entry of entries) {
      const timeAt =
        typeof entry.timeAt === "number"
          ? Temporal.Instant.fromEpochMilliseconds(entry.timeAt)
          : entry.timeAt;
      if (!isWithinTimeWindow(timeAt, entry.timeZone, time)) {
        continue;
      }

      const foodId = entry.food.id;

      const existing = byFoodId.get(foodId);
      if (existing) {
        existing.frequency++;
        if (Temporal.Instant.compare(timeAt, existing.mostRecentTimeAt) > 0) {
          existing.entry = entry;
          existing.mostRecentTimeAt = timeAt;
        }
      } else {
        byFoodId.set(foodId, {
          entry,
          frequency: 1,
          mostRecentTimeAt: timeAt,
        });
      }
    }

    const sorted = Array.from(byFoodId.values()).sort((a, b) => {
      if (b.frequency !== a.frequency) {
        return b.frequency - a.frequency;
      }
      return Temporal.Instant.compare(b.mostRecentTimeAt, a.mostRecentTimeAt);
    });

    return sorted.slice(0, SUGGESTIONS_LIMIT).map(({ entry }) => entry);
  }

  function viewable(eb: ExpressionBuilder<"foodLogEntry", Schema>, actor: UserActor) {
    return eb.or(
      eb.cmp("userId", "=", actor.userId),
      eb.exists("grantsByUser", (q) =>
        AccessGrant.matching(
          q,
          actor,
          AccessGrant.criteria("user", "food_logs", ["editor", "viewer"]),
        ),
      ),
    );
  }

  async function canEdit(tx: Transaction, actor: UserActor, entryId: string): Promise<boolean> {
    const entry = await tx.run(zql.foodLogEntry.where("id", "=", entryId).one());
    if (!entry) {
      return false;
    }
    if (entry.userId === actor.userId) {
      return true;
    }
    const result = await tx.run(
      AccessGrant.matching(
        zql.accessGrant.where("resourceId", "=", entry.userId),
        actor,
        AccessGrant.criteria("user", "food_logs", ["editor"]),
      ).one(),
    );
    return !!result;
  }

  async function canEditOnUser(
    tx: Transaction,
    actor: UserActor,
    userId: string,
  ): Promise<boolean> {
    if (actor.userId === userId) {
      return true;
    }
    const result = await tx.run(
      AccessGrant.matching(
        zql.accessGrant.where("resourceId", "=", userId),
        actor,
        AccessGrant.criteria("user", "food_logs", ["editor"]),
      ).one(),
    );
    return !!result;
  }

  async function assertCanCreateEntries(
    tx: Transaction,
    actor: UserActor,
    userId: string,
    entries: Array<{ foodServingId: string }>,
  ) {
    const hasPermission = await canEditOnUser(tx, actor, userId);
    if (!hasPermission) {
      accessDenied("You do not have permission to add a food log to this user");
    }
    const servings = await tx.run(
      zql.foodServing.where("id", "IN", [...new Set(entries.map((entry) => entry.foodServingId))]),
    );
    const foodIds = servings.map((serving) => serving.foodId);
    const canViewFoods = await Food.canViewBatch(tx, actor, foodIds);
    if (!canViewFoods) {
      accessDenied("Cannot add food log entry with food that you do not have permission to view");
    }
  }

  type CreateEntryArgs = z.infer<typeof createEntrySchema>;
  const createEntrySchema = createInsertSchema(foodLogEntry)
    .omit({ timeZone: true, timeAt: true, userId: true, sourceGroupId: true })
    .extend({
      amount: z.number().positive().max(999_999),
      timeAt: temporalInstantSchema.optional(),
      timeCreated: temporalInstantSchema.optional(),
      timeUpdated: temporalInstantSchema.optional(),
      timeArchived: temporalInstantSchema.nullable().optional(),
    });

  export type CreateEntries = z.infer<typeof createEntries.schema>;
  export const createEntries = withZeroTransaction(
    z.object({
      userId: z.uuid(),
      timeAt: temporalInstantSchema,
      timeZone: zTimezone(),
      entries: createEntrySchema.array(),
    }),
    async ({ tx, ctx: { actor }, args }) => {
      await assertCanCreateEntries(tx, actor, args.userId, args.entries);

      const timeNow = Temporal.Now.instant();
      await Promise.all(
        args.entries.map(async (entry) => {
          await tx.mutate.foodLogEntry.insert({
            id: entry.id,
            foodServingId: entry.foodServingId,
            amount: entry.amount,
            userId: args.userId,
            timeAt: args.timeAt,
            timeZone: args.timeZone,
            timeCreated: timeNow,
            timeUpdated: timeNow,
          });
        }),
      );
    },
  );

  export type SourceAdapter = {
    buildEntries: (
      tx: Transaction,
      actor: UserActor,
      sourceId: string,
      userId: string,
      entryIds: string[],
    ) => Promise<CreateEntryArgs[]>;
  };

  const sourceGroupAdapters: Record<SourceType, SourceAdapter> = {
    planned_meal_occurrence: MealPlan.foodLogSourceAdapter,
    planned_meal_event_assignment_v2: PlannedMeal.foodLogSourceAdapter,
  };

  async function deleteSourceGroupEntries(tx: Transaction, sourceGroupId: string) {
    const entries = await tx.run(zql.foodLogEntry.where("sourceGroupId", "=", sourceGroupId));
    await Promise.all(entries.map((entry) => tx.mutate.foodLogEntry.delete({ id: entry.id })));
  }

  async function getEditableSourceGroup(tx: Transaction, actor: UserActor, sourceGroupId: string) {
    const sourceGroup = await tx.run(
      zql.foodLogSourceGroup.where("id", "=", sourceGroupId).related("entries").one(),
    );
    if (!sourceGroup) {
      throw new Error("Food log source group not found");
    }
    const hasPermission = await canEditOnUser(tx, actor, sourceGroup.userId);
    if (!hasPermission) {
      accessDenied("You do not have permission to edit this food log source group");
    }
    return sourceGroup;
  }

  export type CreateSourceGroup = z.infer<typeof createSourceGroup.schema>;
  export const createSourceGroup = withZeroTransaction(
    z.object({
      id: z.uuid(),
      userId: z.uuid(),
      sourceType: sourceTypeSchema,
      sourceId: z.uuid(),
      timeAt: temporalInstantSchema,
      timeZone: zTimezone(),
      entryIds: z.uuid().array(),
    }),
    async ({ tx, ctx: { actor }, args }) => {
      const existingSourceGroup = await tx.run(
        zql.foodLogSourceGroup
          .where("sourceType", "=", args.sourceType)
          .where("sourceId", "=", args.sourceId)
          .one(),
      );
      if (existingSourceGroup) {
        throw new Error("Food log source group already exists for this food log source");
      }

      const adapter = sourceGroupAdapters[args.sourceType];
      const entries = await adapter.buildEntries(
        tx,
        actor,
        args.sourceId,
        args.userId,
        args.entryIds,
      );
      await assertCanCreateEntries(tx, actor, args.userId, entries);

      const timeNow = Temporal.Now.instant();
      await tx.mutate.foodLogSourceGroup.insert({
        id: args.id,
        userId: args.userId,
        sourceType: args.sourceType,
        sourceId: args.sourceId,
        timeAt: args.timeAt,
        timeZone: args.timeZone,
        timeCreated: timeNow,
        timeUpdated: timeNow,
      });

      await Promise.all(
        entries.map(async (entry) => {
          await tx.mutate.foodLogEntry.insert({
            id: entry.id,
            foodServingId: entry.foodServingId,
            amount: entry.amount,
            userId: args.userId,
            sourceGroupId: args.id,
            timeAt: args.timeAt,
            timeZone: args.timeZone,
            timeCreated: timeNow,
            timeUpdated: timeNow,
          });
        }),
      );
    },
  );

  export type DeleteSourceGroup = z.infer<typeof deleteSourceGroup.schema>;
  export const deleteSourceGroup = withZeroTransaction(
    z.object({ id: z.uuid() }),
    async ({ tx, ctx: { actor }, args }) => {
      const sourceGroup = await getEditableSourceGroup(tx, actor, args.id);
      await deleteSourceGroupEntries(tx, sourceGroup.id);
      await tx.mutate.foodLogSourceGroup.delete({ id: sourceGroup.id });
    },
  );

  export type ResetSourceGroup = z.infer<typeof resetSourceGroup.schema>;
  export const resetSourceGroup = withZeroTransaction(
    z.object({ id: z.uuid(), entryIds: z.uuid().array() }),
    async ({ tx, ctx: { actor }, args }) => {
      const sourceGroup = await getEditableSourceGroup(tx, actor, args.id);
      const adapter = sourceGroupAdapters[sourceGroup.sourceType];
      const entries = await adapter.buildEntries(
        tx,
        actor,
        sourceGroup.sourceId,
        sourceGroup.userId,
        args.entryIds,
      );

      if (Food.itemsEqual(sourceGroup.entries, entries)) {
        return;
      }

      await assertCanCreateEntries(tx, actor, sourceGroup.userId, entries);
      await deleteSourceGroupEntries(tx, sourceGroup.id);

      const timeNow = Temporal.Now.instant();
      await Promise.all(
        entries.map((entry) =>
          tx.mutate.foodLogEntry.insert({
            id: entry.id,
            foodServingId: entry.foodServingId,
            amount: entry.amount,
            userId: sourceGroup.userId,
            sourceGroupId: sourceGroup.id,
            timeAt: sourceGroup.timeAt,
            timeZone: sourceGroup.timeZone,
            timeCreated: timeNow,
            timeUpdated: timeNow,
          }),
        ),
      );

      await tx.mutate.foodLogSourceGroup.update({
        id: sourceGroup.id,
        timeUpdated: timeNow,
      });
    },
  );

  export type EditEntry = z.infer<typeof editEntry.schema>;
  export const editEntry = withZeroTransaction(
    z.object({
      id: z.uuidv7(),
      foodServingId: z.uuidv7().optional(),
      amount: z.number().positive().max(999_999).optional(),
      timeZone: zTimezone().optional(),
      timeAt: temporalInstantSchema.optional(),
    }),
    async ({ tx, ctx: { actor }, args }) => {
      const entry = await tx.run(
        zql.foodLogEntry.where("id", "=", args.id).related("sourceGroup").one(),
      );
      const hasPermission = await canEdit(tx, actor, args.id);
      if (!hasPermission) {
        accessDenied("You do not have permission to edit this food log");
      }
      if (entry?.sourceGroupId && (args.timeAt !== undefined || args.timeZone !== undefined)) {
        accessDenied("Cannot edit time on a source-backed food log entry");
      }
      if (entry && args.foodServingId && args.foodServingId !== entry.foodServingId) {
        await assertCanCreateEntries(tx, actor, entry.userId, [
          { foodServingId: args.foodServingId },
        ]);
      }
      const timeNow = Temporal.Now.instant();
      return tx.mutate.foodLogEntry.update({
        id: args.id,
        sourceGroupId: entry?.sourceGroupId,
        foodServingId: args.foodServingId,
        amount: args.amount,
        timeZone: entry?.sourceGroupId ? undefined : args.timeZone,
        timeAt: entry?.sourceGroupId ? undefined : args.timeAt,
        timeUpdated: timeNow,
      });
    },
  );

  export type DeleteEntry = z.infer<typeof deleteEntry.schema>;
  export const deleteEntry = withZeroTransaction(
    z.object({ id: z.uuid() }),
    async ({ tx, ctx: { actor }, args }) => {
      const entry = await tx.run(zql.foodLogEntry.where("id", "=", args.id).one());
      const hasPermission = await canEdit(tx, actor, args.id);
      if (!hasPermission) {
        accessDenied("You do not have permission to delete this food log");
      }
      await tx.mutate.foodLogEntry.delete({ id: args.id });

      if (!entry?.sourceGroupId) {
        return;
      }

      const remainingEntry = await tx.run(
        zql.foodLogEntry.where("sourceGroupId", "=", entry.sourceGroupId).one(),
      );
      if (!remainingEntry) {
        await tx.mutate.foodLogSourceGroup.delete({ id: entry.sourceGroupId });
      }
    },
  );
}
